import React, { useEffect, useState } from "react";
import { Card, Table, Button, Row, Col, Form } from "react-bootstrap";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const BASE_URL = "https://sutmmg.codydeasoftech.in/api";

const SchoolWiseMeasurementReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [schoolList, setSchoolList] = useState([]);
  const [reportData, setReportData] = useState(null);

  const [filters, setFilters] = useState({
    school_id: "",
  });

  const today = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  const handle401 = (err) => {
    if (err.response?.status === 401) {
      localStorage.clear();
      Swal.fire("Session Expired", "Please login again", "warning");
      navigate("/suda/login");
    }
  };

  // Fetch schools list
  const loadSchools = async () => {
    try {
      const res = await axios.post(`${BASE_URL}/schoolList`, {}, getAuthHeader());
      setSchoolList(res.data?.data || []);
    } catch (err) {
      console.error("School load error:", err);
      if (err.response?.status === 401) {
        handle401(err);
      } else {
        Swal.fire("Alert!", "Failed to load schools", "error");
      }
    }
  };

  // Fetch school wise measurement report
  const loadSchoolReport = async () => {
    if (!filters.school_id) {
      Swal.fire("Alert", "Please select a School", "warning");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/schoolWiseMeasurementReport`,
        { school_id: filters.school_id },
        getAuthHeader()
      );

      if (res.data?.status) {
        setReportData(res.data.data);
        setIsSearched(true);
      } else {
        setReportData(null);
        setIsSearched(true);
        Swal.fire("No Data", res.data?.message || "No records found", "info");
      }
    } catch (err) {
      console.error("Report load error:", err);
      if (err.response?.status === 401) {
        handle401(err);
      } else {
        Swal.fire("Error", "Failed to load measurement report", "error");
      }
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSchools();
  }, []);

  const handleExportExcel = () => {
    if (!reportData || !reportData.report?.length) {
      Swal.fire("No Data", "Nothing to export", "warning");
      return;
    }

    const schoolName = reportData.school_name?.replace(/\s+/g, "_") || "School_Report";
    const formattedDate = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).replace(/\//g, "-");

    const formattedData = reportData.report.map((row, index) => ({
      "SL No": index + 1,
      "Class": row.name || "-",
      "Boys Count": row.boys_count || 0,
      "Girls Count": row.girls_count || 0,
      "Others Count": row.others_count || 0,
      "Total Students": row.total_count || 0,
      "Measurement Taken Boys": row.measurement_taken_boys || 0,
      "Measurement Taken Girls": row.measurement_taken_girls || 0,
      "Measurement Taken Others": row.measurement_taken_others || 0,
      "Measurement Taken Total": row.measurement_taken_total_count || 0,
      "Boys Half Shirt": row.boys_half_shirt || 0,
      "Boys Full Shirt": row.boys_full_shirt || 0,
      "Boys Half Pant": row.boys_half_pant || 0,
      "Boys Full Pant": row.boys_full_pant || 0,
      "Boys Kurta": row.boys_kurta || 0,
      "Boys Pyjama": row.boys_pyjama || 0,
      "Girls Half Shirt": row.girls_half_shirt || 0,
      "Girls Full Shirt": row.girls_full_shirt || 0,
      "Girls Skirt": row.girls_skirt || 0,
      "Girls Salwar": row.girls_salwar || 0,
      "Girls Kameez": row.girls_kameez || 0,
      "Girls Tunic": row.girls_tunic || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "School Measurement Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    const fileName = `School_Measurement_Report_${schoolName}_${formattedDate}.xlsx`;
    saveAs(data, fileName);
  };

  const theadStyle = {
    padding: "8px",
    fontWeight: "600",
    borderBottom: "1px solid black",
    borderTop: "1px solid black",
    textAlign: "center",
    backgroundColor: "#edf7fe",
  };

  const subHeaderStyle = {
    padding: "6px",
    fontWeight: "600",
    borderBottom: "1px solid black",
    borderTop: "1px solid black",
    textAlign: "center",
    backgroundColor: "#e3f2fd",
    fontSize: "12px",
  };

  const studentCountStyle = {
    backgroundColor: "#fbc100",
    textAlign: "center",
  };

  const measurementTakenStyle = {
    backgroundColor: "#fcfb00",
    textAlign: "center",
  };

  const measurementDetailsStyle = {
    backgroundColor: "#91d04e",
    textAlign: "center",
  };

  const calculateTotals = () => {
    if (!reportData?.report?.length) return null;

    const totals = reportData.report.reduce((acc, row) => ({
      boys_count: acc.boys_count + (parseInt(row.boys_count) || 0),
      girls_count: acc.girls_count + (parseInt(row.girls_count) || 0),
      others_count: acc.others_count + (parseInt(row.others_count) || 0),
      total_count: acc.total_count + (parseInt(row.total_count) || 0),
      measurement_taken_boys: acc.measurement_taken_boys + (parseInt(row.measurement_taken_boys) || 0),
      measurement_taken_girls: acc.measurement_taken_girls + (parseInt(row.measurement_taken_girls) || 0),
      measurement_taken_others: acc.measurement_taken_others + (parseInt(row.measurement_taken_others) || 0),
      measurement_taken_total_count: acc.measurement_taken_total_count + (parseInt(row.measurement_taken_total_count) || 0),
      boys_half_shirt: acc.boys_half_shirt + (parseInt(row.boys_half_shirt) || 0),
      boys_full_shirt: acc.boys_full_shirt + (parseInt(row.boys_full_shirt) || 0),
      boys_half_pant: acc.boys_half_pant + (parseInt(row.boys_half_pant) || 0),
      boys_full_pant: acc.boys_full_pant + (parseInt(row.boys_full_pant) || 0),
      boys_kurta: acc.boys_kurta + (parseInt(row.boys_kurta) || 0),
      boys_pyjama: acc.boys_pyjama + (parseInt(row.boys_pyjama) || 0),
      girls_half_shirt: acc.girls_half_shirt + (parseInt(row.girls_half_shirt) || 0),
      girls_full_shirt: acc.girls_full_shirt + (parseInt(row.girls_full_shirt) || 0),
      girls_skirt: acc.girls_skirt + (parseInt(row.girls_skirt) || 0),
      girls_salwar: acc.girls_salwar + (parseInt(row.girls_salwar) || 0),
      girls_kameez: acc.girls_kameez + (parseInt(row.girls_kameez) || 0),
      girls_tunic: acc.girls_tunic + (parseInt(row.girls_tunic) || 0),
    }), {
      boys_count: 0, girls_count: 0, others_count: 0, total_count: 0,
      measurement_taken_boys: 0, measurement_taken_girls: 0, measurement_taken_others: 0, measurement_taken_total_count: 0,
      boys_half_shirt: 0, boys_full_shirt: 0, boys_half_pant: 0, boys_full_pant: 0, boys_kurta: 0, boys_pyjama: 0,
      girls_half_shirt: 0, girls_full_shirt: 0, girls_skirt: 0, girls_salwar: 0, girls_kameez: 0, girls_tunic: 0,
    });

    return totals;
  };

  const totals = calculateTotals();

  const selectedSchoolName = schoolList.find(s => String(s.id) === String(filters.school_id))?.name || "";

  return (
    <div className="container mt-4" style={{ maxWidth: 1600 }}>
      <Card
        className="mb-5 border-0"
        style={{
          background: "linear-gradient(135deg, #E3F2FD, #ffffff)",
          borderRadius: "14px",
          boxShadow: "0 8px 25px rgba(21,101,193,0.15)",
        }}
      >
        <div
          className="px-4 py-3 text-white rounded-top"
          style={{
            background: "linear-gradient(90deg, #0D47A1, #1565C1)",
          }}
        >
          <h5 className="mb-0 fw-bold">
            <i className="fa-solid fa-filter"></i> School Wise Measurement Report
          </h5>
        </div>

        <Card.Body className="px-4 py-4">
          <Row className="align-items-center">
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-dark">
                  School <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={filters.school_id}
                  onChange={(e) => {
                    const schoolId = e.target.value;
                    setFilters({ school_id: schoolId });
                    setReportData(null);
                    setIsSearched(false);
                  }}
                  style={{
                    borderRadius: "10px",
                    border: "2px solid #1565C1",
                  }}
                >
                  <option value="">Select School</option>
                  {schoolList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4} className="d-flex gap-3 mt-3">
              <Button
                className="w-50 fw-semibold text-white"
                style={{
                  background: "#1565C1",
                  border: "none",
                  borderRadius: "10px",
                  transition: "0.3s",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#0D47A1")}
                onMouseLeave={(e) => (e.target.style.background = "#1565C1")}
                onClick={loadSchoolReport}
              >
                <i className="fa-solid fa-magnifying-glass me-2"></i> Search
              </Button>

              <Button
                className="w-50 fw-semibold text-white"
                style={{
                  background: "#E53935",
                  border: "none",
                  borderRadius: "10px",
                  transition: "0.3s",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#D32F2F")}
                onMouseLeave={(e) => (e.target.style.background = "#E53935")}
                onClick={() => {
                  const resetFilters = { school_id: "" };
                  setFilters(resetFilters);
                  setReportData(null);
                  setIsSearched(false);
                }}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i> Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {reportData && reportData.report?.length > 0 && (
        <div className="d-flex justify-content-between align-items-center my-4">
          <Button
            style={{
              background: "#43A047",
              border: "none",
              borderRadius: "8px",
            }}
            onClick={handleExportExcel}
          >
            <i className="fa-solid fa-file-excel me-2"></i> Export to Excel
          </Button>

          <div className="text-center">
            <h4 className="fw-bold text-dark mb-0">{reportData.school_name || selectedSchoolName}</h4>
            <div className="fs-6 text-success">
              School-wise Measurement Report (As on {today})
            </div>
          </div>

          <div style={{ width: "140px" }}></div>
        </div>
      )}

      {/* TABLE */}
      <Card
        className="border-0"
        style={{
          boxShadow: "0 10px 35px rgba(21,101,193,0.18)",
          overflow: "hidden",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <Table bordered hover responsive striped className="mb-0" style={{ minWidth: "1200px" }}>
            <thead>
              <tr>
                <th rowSpan="2" style={theadStyle}>Class</th>
                <th colSpan="4" style={theadStyle}>Student Count</th>
                <th colSpan="4" style={theadStyle}>Measurements Taken</th>
                <th colSpan="6" style={theadStyle}>Boys Measurements</th>
                <th colSpan="6" style={theadStyle}>Girls Measurements</th>
              </tr>
              <tr>
                <th style={subHeaderStyle}>Boys</th>
                <th style={subHeaderStyle}>Girls</th>
                <th style={subHeaderStyle}>Others</th>
                <th style={subHeaderStyle}>Total</th>
                <th style={subHeaderStyle}>Boys</th>
                <th style={subHeaderStyle}>Girls</th>
                <th style={subHeaderStyle}>Others</th>
                <th style={subHeaderStyle}>Total</th>
                <th style={subHeaderStyle}>Half Shirt</th>
                <th style={subHeaderStyle}>Full Shirt</th>
                <th style={subHeaderStyle}>Half Pant</th>
                <th style={subHeaderStyle}>Full Pant</th>
                <th style={subHeaderStyle}>Kurta</th>
                <th style={subHeaderStyle}>Pyjama</th>
                <th style={subHeaderStyle}>Half Shirt</th>
                <th style={subHeaderStyle}>Full Shirt</th>
                <th style={subHeaderStyle}>Skirt</th>
                <th style={subHeaderStyle}>Salwar</th>
                <th style={subHeaderStyle}>Kameez</th>
                <th style={subHeaderStyle}>Tunic</th>
              </tr>
            </thead>
            <tbody>
  {loading ? (
    <tr>
      <td colSpan="21" className="text-center py-4">
        <div className="loader-wrapper">
          <div className="blue-spinner"></div>
          <div className="loading-line">
            <div className="loading-progress"></div>
          </div>
          <p className="loading-text">Please Wait... While Gathering Reports</p>
        </div>
      </td>
    </tr>
  ) : !isSearched ? (
    <tr>
      <td colSpan="21" className="text-center py-5 text-muted">
        Please select a school and click the Search button to view the measurement report.
      </td>
    </tr>
  ) : !reportData?.report?.length ? (
    <tr>
      <td colSpan="21" className="text-center py-5 text-muted">
        No records found
      </td>
    </tr>
  ) : (
    <>
      {reportData.report.map((row, idx) => (
        <tr key={idx}>
          <td className="text-center fw-bold">{row.name || row.code}</td>
          <td style={studentCountStyle}>{row.boys_count || 0}</td>
          <td style={studentCountStyle}>{row.girls_count || 0}</td>
          <td style={studentCountStyle}>{row.others_count || 0}</td>
          <td style={studentCountStyle}>{row.total_count || 0}</td>
          <td style={measurementTakenStyle}>{row.measurement_taken_boys || 0}</td>
          <td style={measurementTakenStyle}>{row.measurement_taken_girls || 0}</td>
          <td style={measurementTakenStyle}>{row.measurement_taken_others || 0}</td>
          <td style={measurementTakenStyle}>{row.measurement_taken_total_count || 0}</td>
          <td style={measurementDetailsStyle}>{row.boys_half_shirt || 0}</td>
          <td style={measurementDetailsStyle}>{row.boys_full_shirt || 0}</td>
          <td style={measurementDetailsStyle}>{row.boys_half_pant || 0}</td>
          <td style={measurementDetailsStyle}>{row.boys_full_pant || 0}</td>
          <td style={measurementDetailsStyle}>{row.boys_kurta || 0}</td>
          <td style={measurementDetailsStyle}>{row.boys_pyjama || 0}</td>
          <td style={measurementDetailsStyle}>{row.girls_half_shirt || 0}</td>
          <td style={measurementDetailsStyle}>{row.girls_full_shirt || 0}</td>
          <td style={measurementDetailsStyle}>{row.girls_skirt || 0}</td>
          <td style={measurementDetailsStyle}>{row.girls_salwar || 0}</td>
          <td style={measurementDetailsStyle}>{row.girls_kameez || 0}</td>
          <td style={measurementDetailsStyle}>{row.girls_tunic || 0}</td>
        </tr>
      ))}
      {totals && (
        <tr style={{ backgroundColor: "#01b1ec", fontWeight: "bold" }}>
          <td className="text-center" style={{ backgroundColor: "#01b1ec", fontWeight: "bold" }}>Total</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.boys_count}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.girls_count}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.others_count}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.total_count}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.measurement_taken_boys}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.measurement_taken_girls}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.measurement_taken_others}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.measurement_taken_total_count}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.boys_half_shirt}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.boys_full_shirt}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.boys_half_pant}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.boys_full_pant}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.boys_kurta}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.boys_pyjama}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.girls_half_shirt}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.girls_full_shirt}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.girls_skirt}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.girls_salwar}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.girls_kameez}</td>
          <td style={{ backgroundColor: "#01b1ec", fontWeight: "bold", textAlign: "center" }}>{totals.girls_tunic}</td>
        </tr>
      )}
    </>
  )}
</tbody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default SchoolWiseMeasurementReport;
